import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useAuth } from 'contexts/AuthContext';
import { PageContainer, CommonCss } from 'styles/common.styles';
import { SEARCH_LABEL_W } from 'styles/theme';
import { FormArea, CollapsibleSearchPanel, ActionButtonGroup } from 'components/forms';
import { getToday } from 'utils/common_util';
import { toDisplayDate } from 'utils/datePickerUtils';
import { showAlert } from 'utils/toastBridge';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import {
  GridExportProvider,
  GridExportFocusScope,
  useRegisterGridExport,
  useExcelExportAction,
} from 'components/grid/excel';
import { useGridPrintAction } from 'components/grid/print';

const GRID_ID = 'appointment-list';
const JOIN_KEYS = ['empNo'];

/**
 * HR-APT PoC — 발령 목록 조회
 * 조회 시점(발령일자=asOfDate) 기준 유효 발령. 기간(M1)이 아닌 단일일(M2).
 */
const AppointmentListInner = () => {
  const gridRef = useRef(null);
  const { user } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);
  const searchDefaultValues = {
    tenantCode,
    empNo: '',
    personNm: '',
    status: '',
    asOfDate: getToday(),
  };

  const methods = useForm({ mode: 'onSubmit', defaultValues: { ...searchDefaultValues } });
  const { getValues: getSearchValues, setValue: setSearchValue, watch } = methods;

  useEffect(() => {
    setSearchValue('tenantCode', tenantCode);
  }, [tenantCode, setSearchValue]);

  const columns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '사번', field: 'empNo', size: 110 }),
      hiCol.roTextColumn({ header: '성명', field: 'personNm', size: 100 }),
      hiCol.roTextColumn({ header: '발령유형', field: 'appointmentTypeNm', size: 120 }),
      hiCol.roTextColumn({ header: '발령일', field: 'appointDate', size: 110 }),
      hiCol.roTextColumn({ header: '시작일', field: 'validFrom', size: 110 }),
      hiCol.roTextColumn({ header: '종료일', field: 'validTo', size: 110 }),
      hiCol.roTextColumn({ header: '직급', field: 'gradeNm', size: 80 }),
      hiCol.roTextColumn({ header: '부서', field: 'orgUnitNm', size: 120 }),
      hiCol.roTextColumn({ header: '직위', field: 'positionTitle', size: 100 }),
      hiCol.roTextColumn({ header: '호봉', field: 'payStep', size: 60 }),
      hiCol.roTextColumn({ header: '상태', field: 'status', size: 90 }),
      hiCol.roTextColumn({ header: '기관', field: 'tenantNm', size: 140 }),
    ],
    []
  );

  const grid = useHiGridHook({
    gridRef,
    getSearchValues,
    selectQuery: 'Appointment.selectAppointmentList',
    autoSearch: true,
  });

  const getData = useCallback(
    () => gridRef.current?.getFilteredData?.() ?? gridRef.current?.getData?.() ?? [],
    []
  );
  const getDataColumns = useCallback(() => gridRef.current?.getDataColumns?.() ?? [], []);

  useRegisterGridExport({
    id: GRID_ID,
    label: '발령 목록',
    getData,
    getDataColumns,
    joinKeys: JOIN_KEYS,
    order: 0,
  });

  const { excelButton, dialog: excelDialog } = useExcelExportAction({ fileName: '발령목록' });
  const { printButton, dialog: printDialog } = useGridPrintAction({
    fileName: '발령목록',
    prefsUserId,
    defaultTitle: '발령 목록',
  });

  const actionButtons = [
    {
      id: 'search',
      label: '조회',
      icon: '🔍',
      onClick: () => grid.handleSearch().catch((e) => showAlert(e.message || '조회 실패', 'error')),
      hotkey: 'F7',
    },
    printButton,
    excelButton,
  ];

  // 발령일자(asOfDate): 해당일 유효 발령 — 기간 아님
  const searchFields = [
    [
      {
        name: 'asOfDate',
        label: '발령일자',
        type: 'date',
        width: CommonCss.w120,
        labelWidth: SEARCH_LABEL_W,
        required: true,
        showTodayShortcut: true,
      },
    ],
  ];

  const asOfDisp = toDisplayDate(watch('asOfDate') || getToday());

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} loading={grid.loading} />
        <CollapsibleSearchPanel
          storageKey="appointment-list"
          prefsUserId={prefsUserId}
          summary={`발령일자 ${asOfDisp}`}
        >
          <FormArea
            fields={searchFields}
            compact
            onFieldEnter={() => grid.handleSearch()}
            defaultValues={searchDefaultValues}
          />
        </CollapsibleSearchPanel>
      </FormProvider>
      <GridExportFocusScope gridId={GRID_ID}>
        <div style={{ flex: 1, minHeight: 0 }}>
          <HiGridComponent
            ref={gridRef}
            columns={columns}
            height="100%"
            showRowNumber
            enableColumnFilters
            enableContextMenu
            enableRowEditMenu={false}
            showModColumn={false}
            enableRowCheck={false}
            enableCut={false}
            enableFindReplace
            allowReplace={false}
            enableHeaderMenu
            enableSelectionStats
            enableFreeze
            enableQuickExport
            exportFileName="발령목록"
            gridId={GRID_ID}
            prefsUserId={prefsUserId}
          />
        </div>
      </GridExportFocusScope>
      {excelDialog}
      {printDialog}
    </PageContainer>
  );
};

const AppointmentList = () => (
  <GridExportProvider>
    <AppointmentListInner />
  </GridExportProvider>
);

export default AppointmentList;
